<template>
  <div :class="consumerClasses" :data-cube="props.consumer.cubeName" :id="`c_${props.consumer.cubeName}`">
    <router-link class="consumer__left" :to="mapRoute">
      <img class="consumer__img" :src="cubeIconUrl(props.consumer, 'svg')" />
      <h4 class="consumer__title">{{ nickName }}</h4>
      <span v-if="unreadNotifications.length > 0" class="consumer__has-notification" :title="t('notifications.title')"
            @click.stop.prevent="showNotifications = !showNotifications"></span>
    </router-link>
    <div class="consumer__content">
      <div class="consumer__content-top">
        <div class="consumer__priority">
          <div class="consumer__priority-title">{{ t('efriends.priority') }}</div>
          <Choice name="priority" :default="connectionPriority" :items="priorities" :use-select="true" :show-edit-button="false" ref="connection-priority" />
          <span v-if="isNonUserLock" class="consumer__priority-locked">
            <IconLock />
            {{ lockTitle }}
          </span>
        </div>
      </div>
      <div class="consumer__content-bottom">
        <output class="consumer__current-price">
          <Price v-if="cube?.productionType?.value != 'eeg'" :label="t('efriends.currentPrice')" :value="currentPrice" :loading="!pricesLoaded" />
          <Price v-else :label="t('efriends.currentPrice')" noValueLabel="efriends.priceFromEEG" />
        </output>
        <output class="consumer__next-price" v-if="pricesLoaded && hasNextPrice">
          <Price :label="nextPriceLabel" :value="nextPrice" />
        </output>
        <a v-if="pricesLoaded && !isNonUserLock" :class="{
          'consumer__edit-price': true,
          'consumer__edit-price--active': isEditingPrice,
        }" @click="onClickEditPrice" :title="t('global.edit')">
          <IconEdit />
        </a>
        <div v-else-if="pricesLoaded" class="consumer__edit-spacer" />
      </div>
      <div :class="{
        'consumer__edit': true,
        'consumer__edit--edit-price': isEditingPrice,
      }">
        <ToggleButton :text="t('efriends.specialPrice')" icon-left="Tag" :is-checked="hasSpecialPricesOriginal"
                      :extra-classes="['consumer__toggle-special-price']" ref="toggle-special-price" />
        <div class="consumer__selling-price">
          <input type="range" :min="minSellingPrice" :max="maxSellingPrice" :step="sellingPriceStepSize" v-model="priceInputModel" :disabled="!hasSpecialPrices"
                 @input="priceChanged = true" />
          <div class="consumer__selling-price-legend">
            <span class="consumer__selling-price-legend-min">{{
              formatPowerPrice(minSellingPrice, false)
            }}</span>
            <span class="consumer__selling-price-legend-text">
              {{ formatPowerPrice(priceInputModel, false) }}
            </span>
            <span class="consumer__selling-price-legend-max">{{
              formatPowerPrice(maxSellingPrice, false)
            }}</span>
          </div>
        </div>
      </div>
      <div :class="{
        'consumer__submit-buttons': true,
        'consumer__submit-buttons--expanded': isChanged,
      }">
        <a class="consumer__cancel" @click="cancel()">{{ t('global.cancel') }}</a>

        <LinkButton type="small" icon-right="Arrow" :form="`consumers_${props.consumer.cubeName}`" :extra-classes="['button--primary consumer__save']"
                    :text="t('global.save')" @click.native="saveChanges()" />
      </div>
    </div>
    <CubeNotifications v-if="showNotifications" :notifications="unreadNotifications" :cube-name="consumer.cubeName"
                       :extra-classes="['consumer__notifications']" />
  </div>
</template>

<script setup lang="ts">
  import LinkButton from '@/components/LinkButton.vue';
  import ToggleButton from '@/components/ToggleButton.vue';
  import Choice from '@/components/Choice.vue';
  import Price from '@/components/Price.vue';
  import IconEdit from '@/components/icons/Edit.vue';
  import CubeNotifications from '@/components/efriends/CubeNotifications.vue';
  import IconLock from '@/components/icons/Lock.vue';
  import { useStrings } from '@/composables/useStrings.ts';
  import { CONNECTION_PRIORITIES } from '@/constants';
  import { useMapStore } from '@/stores/useMapStore';
  import { useSettingsStore } from '@/stores/useSettingsStore';
  import { useCubeStore } from '@/stores/useCubeStore';
  import { computed, ref, toRef, useTemplateRef } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useUnits } from '@/composables/useUnits.ts';
  import { useDate } from '@/composables/useDate.ts';
  import { useSpecificCube } from '@/composables/useSpecificCube';
  import { resolveSpecialPriceState } from '@/util/SpecialPriceResolver';

  const router = useRouter();
  const currentRoute = useRoute();
  const mapStore = useMapStore();
  const settingsStore = useSettingsStore();
  const cubeStore = useCubeStore();
  const { t } = useStrings();
  const { cubeIconUrl } = mapStore;
  const connectionPriorityRef = useTemplateRef('connection-priority');
  const toggleSpecialPriceRef = useTemplateRef('toggle-special-price');
  const { formatPowerPrice } = useUnits();
  const dateHelper = useDate();

  const props = defineProps({
    consumer: {
      type: Object,
      required: true,
    },
  });

  const mapRoute = {
    name: 'map',
    params: {
      selected: props.consumer.cubeName,
    },
  };

  const showNotifications = ref(false);
  const priceInputModel = ref(null);
  const priceChanged = ref(false);
  const isEditingPrice = ref(false);

  const connectionPriority = computed(() => {
    // TODO Clean this up.
    return props.consumer.connectionMetadata.lockType === 'EG'
      ? 'EG'
      : cubeStore.connectionPriorityFromCube(props.consumer);
  });

  const cubeRef = toRef(props.consumer as any);
  const cube = useSpecificCube(router, cubeRef);

  const basePrices = computed(() => settingsStore.defaultSellingPrices);
  const specialPrices = computed(() => {
    return settingsStore.consumerPrices[props.consumer.cubeName]
  });
  const pricesLoaded = computed(() => {
    return settingsStore.defaultSellingPricesLoaded && settingsStore.consumerPricesLoaded;
  });

  const resolvedPriceState = computed(() => {
    return resolveSpecialPriceState(basePrices.value, specialPrices.value, dateHelper.today());
  });

  const hasSpecialPricesOriginal = computed(() => {
    if (!pricesLoaded.value) {
      return false;
    }
    return resolvedPriceState.value.hasEnabledSpecialPrice;
  });

  const hasSpecialPrices = computed(() => {
    if (!pricesLoaded.value) {
      return false;
    }

    if (
      toggleSpecialPriceRef.value &&
      toggleSpecialPriceRef.value.checkedValue !== hasSpecialPricesOriginal.value
    ) {
      return toggleSpecialPriceRef.value.checkedValue;
    }

    return hasSpecialPricesOriginal.value;
  });

  const hasSpecialPricesChanged = computed(() => {
    if (!pricesLoaded.value) {
      return false;
    }
    return hasSpecialPrices.value !== hasSpecialPricesOriginal.value;
  });

  const onClickEditPrice = () => {
    isEditingPrice.value = !isEditingPrice.value;

    if (isEditingPrice.value) {
      connectionPriorityRef.value.showSelect = true;
      priceInputModel.value = editablePrice.value;
    }
    else {
      connectionPriorityRef.value.showSelect = false;
      priceInputModel.value = null;
      priceChanged.value = false;
    }
  };

  const currentPrice = computed(() => {
    return resolvedPriceState.value.currentPrice;
  });

  const nextPrice = computed(() => {
    return resolvedPriceState.value.nextPrice;
  });

  const hasNextPrice = computed(() => {
    return resolvedPriceState.value.hasNextPrice;
  });

  const editablePrice = computed(() => {
    if (resolvedPriceState.value.nextSource === 'special' && resolvedPriceState.value.nextPrice !== null) {
      return resolvedPriceState.value.nextPrice;
    }
    if (resolvedPriceState.value.currentSource === 'special') {
      return resolvedPriceState.value.currentPrice;
    }
    return resolvedPriceState.value.currentPrice;
  });

  const isChanged = computed(() => priceChanged.value || hasSpecialPricesChanged.value || connectionPriorityChanged.value);

  // Note that the next price does not necessarily have to be a special price if the current
  // price is a special price.
  const dateNextPrice = computed(() => {
    return resolvedPriceState.value.nextDate || false;
  });

  const consumerClasses = computed(() => {
    let result = ['consumer'];
    const cubeNameRegex = new RegExp(`c_${props.consumer.cubeName}`);
    if (
      currentRoute.name === 'efriends' &&
      currentRoute.hash.match(cubeNameRegex)
    ) {
      result.push('consumer--highlighted');
    }
    return result.join(' ');
  });

  const minSellingPrice = computed(() => settingsStore.minDefaultSellingPrice);
  const maxSellingPrice = computed(() => settingsStore.maxDefaultSellingPrice);
  const sellingPriceStepSize = computed(() => settingsStore.sellingPriceStepSize);

  const priorities = computed(() => {
    if (props.consumer.connectionMetadata.lockType === 'EG') {
      return [{ title: 'EG', value: 'EG' }];
    }
    return CONNECTION_PRIORITIES;
  });

  const nickName = computed(() => props.consumer.params.nickName);
  const nextPriceLabel = computed(() => {
    if (dateNextPrice.value) {
      return t('efriends.nextPriceFrom', { '@date': dateHelper.formatDate(dateNextPrice.value, 'date') });
    }
    else {
      return t('efriends.nextPriceLabel');
    }
  });

  const unreadNotifications = computed(() => {
    return cubeStore.notifications.filter((notification) => {
      return notification.triggeringcubename === props.consumer.cubeName && notification.unread === 1;
    });
  });

  const lockTitle = computed(() => {
    return t(
      'efriends.lock.lockTypes.' + props.consumer.connectionMetadata.lockType
    );
  });

  const isNonUserLock = computed(() => {
    return (
      props.consumer.connectionMetadata.isLocked === true &&
      props.consumer.connectionMetadata.lockType !== 'user'
    );
  });

  const saveChanges = async () => {
    let promises = [];
    if (hasSpecialPrices.value) {
      promises.push(
        settingsStore.saveSpecialSellingPriceForConsumer({
          price: parseFloat(priceInputModel.value),
          consumer: props.consumer.cubeName,
        })
      );
    }

    if (hasSpecialPricesOriginal.value && !hasSpecialPrices.value) {
      // Remove the special price.
      promises.push(
        settingsStore.removeSpecialPriceForConsumer(props.consumer.cubeName)
      );
    }

    if (connectionPriorityChanged.value) {
      const setPriorityPromise = cubeStore.setConnectionPriority({
        cubeName: props.consumer.cubeName,
        priority: connectionPriorityRef.value.value,
        forConsumers: true,
      });
      promises.push(setPriorityPromise);
    }

    await Promise.all(promises);

    setTimeout(() => {
      cancel();
    }, 10);
  };

  const connectionPriorityChanged = computed(() => {
    return connectionPriorityRef.value && connectionPriority.value !== connectionPriorityRef.value.value;
  });

  const cancel = () => {
    connectionPriorityRef.value.showSelect = false;
    connectionPriorityRef.value.value = connectionPriority.value;
    toggleSpecialPriceRef.value.checkedValue = hasSpecialPricesOriginal.value;
    isEditingPrice.value = false;
    priceInputModel.value = null;
    priceChanged.value = false;
  };
</script>

<style lang="scss" scoped>
  .consumer {
    padding: $spacer10 0;
    position: relative;
    display: flex;
    flex-wrap: wrap;

    &--highlighted {
      background: $grey-lightest;
    }

    &__left {
      margin-right: $spacer10;
      text-align: center;
      transition: $transition-base;
      width: $cube-list-img-size-min;
      flex-shrink: 0;
      cursor: pointer;
      position: relative;

      @media (min-width: $bp-xs) {
        width: $cube-list-img-size-xs;
      }

      @media (min-width: $bp-sm) {
        flex-basis: $efriends-page-icon-width-sm;
      }
    }

    &__img {
      padding: 0;
      max-width: 100%;

      @media (min-width: $bp-ssm) {
        padding: 0 $spacer05;
      }

      @media (min-width: $bp-sm) {
        padding: 0 $spacer20;
      }
    }

    &__title {
      margin: 0;
      text-align: center;
      font-size: $font-size-sm;
      line-height: $line-height-min;
      transition: $transition-base;
      font-weight: 500;

      @media (min-width: $bp-ssm) {
        font-size: $font-size-base;
      }
    }

    &__has-notification {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 0.75em;
      height: 0.75em;
      top: $spacer01 * -1;
      left: $spacer01 * -1;
      position: absolute;
      background: $notification-color;
      padding: 0.25em;
      border-radius: 100%;
      color: $white;

      @media (min-width: $bp-sm) {
        top: $spacer05;
        left: $spacer10;
        width: 1em;
        height: 1em;
      }
    }

    &__label {
      font-size: $font-size-sm;
      color: $grey-dark;

      @media (min-width: $bp-xs) {
        font-size: $font-size-base;
      }
    }

    &__toggle-special-price {
      margin-top: $spacer05;
      margin-left: auto;

      @media (min-width: $bp-md) {
        float: right;
        margin-right: 0;
      }
    }

    &__priority {
      margin-right: $spacer05;

      &-title {
        font-size: $font-size-sm;
        color: $grey-dark;
        line-height: $line-height-sm;
      }

      &-locked {
        margin-left: $spacer05;
        vertical-align: top;
      }
    }

    &__edit-spacer {
      justify-self: flex-end;
      align-self: flex-end;
      margin-bottom: $spacer01;
      transition: $transition-base;
      padding: 0 $spacer025;
      border-radius: $border-radius;
      margin-left: auto;
      flex: 0 0 2em;

      @media (min-width: $bp-sm) {
        padding: $spacer025 $spacer05;
      }

      @media (min-width: $bp-md) {
        margin-left: 0;
        align-self: flex-start;
        margin-top: $spacer10;
      }
    }

    &__edit-price {
      justify-self: flex-end;
      cursor: pointer;
      align-self: flex-end;
      margin-bottom: $spacer01;
      transition: $transition-base;
      padding: 0 $spacer025;
      border-radius: $border-radius;
      margin-left: auto;

      @media (min-width: $bp-sm) {
        padding: $spacer025 $spacer05;
      }

      @media (min-width: $bp-md) {
        margin-left: 0;
        align-self: flex-start;
        margin-top: $spacer10;
      }

      &--active {
        background: $grey-dark;
        color: $white;
      }

      @include hover-focus-active {
        background: $grey-dark;
        color: $white;
      }

      .icon {
        width: 1em;
        vertical-align: middle;
        // Vertical visual alignment.
        margin-top: $spacer01 * -1;
      }
    }

    &__content {
      flex-grow: 1;

      @media (min-width: $bp-md) {
        display: flex;
        flex-wrap: wrap;
        // Only when setting a width that is smaller than the available space
        // this will fit onto the same line as left.
        width: 30%;
      }

      &-top {
        display: flex;
        flex-wrap: wrap;
      }

      &-bottom {
        margin-top: $spacer10;
        display: flex;
        justify-self: flex-end;
        margin-left: auto;
        flex-grow: 1;
        flex-basis: 40%;

        @media (min-width: $bp-xs) {
          justify-content: flex-end;
        }

        @media (min-width: $bp-md) {
          margin-top: 0;
        }
      }
    }

    &__selling-price {
      &-legend {
        display: flex;
        justify-content: space-between;
      }
    }

    &__edit {
      max-height: 0;
      overflow: hidden;
      transition: $transition-base;
      margin-bottom: $spacer05;

      @media (min-width: $bp-sm) {
        flex-basis: 100%;
      }

      &--edit-price {
        max-height: 200px;
      }

      &-bottom {
        margin-top: $spacer05;
        display: flex;
        justify-content: space-between;
      }
    }

    &__submit-buttons {
      max-height: 0;
      overflow: hidden;
      transition: $transition-base;
      display: flex;
      justify-content: space-between;
      align-items: center;

      @media (min-width: $bp-md) {
        justify-content: flex-end;
        flex-basis: 100%;
      }

      &--expanded {
        max-height: 60px;
      }
    }

    &__cancel {
      cursor: pointer;
      transition: $transition-base;
    }

    &__save {
      margin-left: $spacer10;
    }
  }


  .icon {
    width: 0.8rem;

    &--spinner {
      animation: spin 1.8s linear 0s infinite;
    }
  }

</style>
